import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Users, FileText, BarChart3, Settings, LogOut, Shield, CheckCircle, XCircle, Clock, Search, Filter, Ban, Trash2, RotateCcw, AlertTriangle, History, Mail} from 'lucide-react'
import DashboardSwitcher from "../components/DashboardSwitcher"

type Tab = "overview" | "nurses" | "messages" | "recruiters" | "affiliates" | "reports" | "audit" | "settings" | "contacts"

interface NurseProfile {
  _id: string
  userId: string
  fullName?: string
  email?: string
  country?: string
  specialties?: string[]
  experienceYears?: string
  nclexStatus?: string
  cgfnsStatus?: string
  dataflowStatus?: string
  role?: string
  verified?: boolean
  blocked?: boolean
  status?: string
  profileCompletion?: number
}

interface ActionModal {
  type: 'unverify' | 'reject' | 'block' | 'delete' | null
  nurse: NurseProfile | null
}

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>("overview")
  const [nurses, setNurses] = useState<NurseProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState<string>("all")
  const [stats, setStats] = useState({
    total_nurses: 0,
    nurses_7d: 0,
    verified_nurses: 0,
    pending_verification: 0,
    nclex_passed: 0,
    cgfns_completed: 0,
    dataflow_completed: 0
  })
  const [actionModal, setActionModal] = useState<ActionModal>({ type: null, nurse: null })
  const [actionReason, setActionReason] = useState("")
  const [notifyNurse, setNotifyNurse] = useState(true)
  const [permanentBan, setPermanentBan] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [selectedNurses, setSelectedNurses] = useState<string[]>([])
  const [auditLogs, setAuditLogs] = useState<any[]>([])
  const [auditLoading, setAuditLoading] = useState(false)



  useEffect(() => {
    loadData()
    // Auto-refresh every 45 seconds
    const interval = setInterval(loadData, 45000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (activeTab === "audit") {
      loadAuditLogs()
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab === "messages") {
      navigate("/dashboard/admin/messages")
    }
  }, [activeTab, navigate])

  const loadData = async () => {
    await loadNurses()
  }

  const loadAuditLogs = async () => {
    try {
      setAuditLoading(true)
      const result = await lumi.entities.AuditLogs.list({
        limit: 100,
        sort: { timestamp: -1 }
      })
      setAuditLogs(result.list)
    } catch (error) {
      console.error("Failed to load audit logs:", error)
      toast.error("Failed to load audit logs")
    } finally {
      setAuditLoading(false)
    }
  }

  const loadNurses = async () => {
    try {
      setLoading(true)
      
      // Load all profiles - will show all nurse profiles
      const result = await lumi.entities.UserProfiles.list({ 
        limit: 1000,
        sort: { createdAt: -1 }
      })

      console.log("Loaded profiles:", result.list.length)
      const nursesList = result.list as NurseProfile[]
      setNurses(nursesList)

      // Calculate stats
      const now = new Date()
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      
      const totalNurses = nursesList.length
      const nurses7d = nursesList.filter((n: any) => {
        if (!n.createdAt) return false
        return new Date(n.createdAt) >= sevenDaysAgo
      }).length
      const verifiedNurses = nursesList.filter(n => n.verified).length
      const pendingVerification = totalNurses - verifiedNurses
      const nclexPassed = nursesList.filter(n => n.nclexStatus === "Passed").length
      const cgfnsCompleted = nursesList.filter(n => n.cgfnsStatus === "Passed").length
      const dataflowCompleted = nursesList.filter(n => n.dataflowStatus === "Complete").length

      setStats({
        total_nurses: totalNurses,
        nurses_7d: nurses7d,
        verified_nurses: verifiedNurses,
        pending_verification: pendingVerification,
        nclex_passed: nclexPassed,
        cgfns_completed: cgfnsCompleted,
        dataflow_completed: dataflowCompleted
      })
    } catch (error) {
      console.error("Failed to load nurses:", error)
      toast.error("Failed to load nurse profiles")
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyNurse = async (nurseId: string) => {
    try {
      const nurse = nurses.find(n => n._id === nurseId)
      if (!nurse) {
        toast.error("Nurse not found")
        return
      }

      const adminEmail = lumi.auth.user?.email || "Unknown Admin"
      const now = new Date().toISOString()

      // Update nurse profile directly
      await lumi.entities.UserProfiles.update(nurseId, {
        verified: true,
        verifiedAt: now,
        verifiedBy: adminEmail,
        status: "active"
      })

      // Create audit log
      await lumi.entities.AuditLogs.create({
        actor_id: lumi.auth.user?.userId || "",
        action: "nurse_verified",
        target_id: nurseId,
        metadata: {
          timestamp: now,
          nurseName: nurse.fullName,
          nurseEmail: nurse.email
        }
      })

      // Send email notification
      if (nurse.email) {
        await lumi.tools.email.send({
          to: nurse.email,
          subject: "Nursing Hive - Profile Verified!",
          fromName: "Nursing Hive Team",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #0FB5AE;">Congratulations! Your Profile is Verified</h2>
              <p>Dear ${nurse.fullName || "Nurse"},</p>
              <p>Your Nursing Hive profile has been verified by our admin team.</p>
              <p>You can now opt-in to be visible to recruiters and start receiving job opportunities.</p>
              <p>Best regards,<br/>Nursing Hive Team</p>
            </div>
          `
        }).catch(err => console.error("Email failed:", err))
      }

      toast.success("Nurse verified successfully")
      setSelectedNurses([])
      loadNurses()
    } catch (error: any) {
      console.error("Failed to verify nurse:", error)
      toast.error(error?.message || "Failed to verify nurse")
    }
  }

  const handleBulkVerify = async () => {
    if (selectedNurses.length === 0) {
      toast.error("No nurses selected")
      return
    }

    if (!confirm(`Verify ${selectedNurses.length} nurse(s)?`)) return

    try {
      for (const nurseId of selectedNurses) {
        await handleVerifyNurse(nurseId)
      }
      toast.success(`${selectedNurses.length} nurse(s) verified`)
      setSelectedNurses([])
    } catch (error) {
      console.error("Bulk verify failed:", error)
      toast.error("Bulk verification failed")
    }
  }

  const toggleNurseSelection = (nurseId: string) => {
    setSelectedNurses(prev => 
      prev.includes(nurseId) 
        ? prev.filter(id => id !== nurseId)
        : [...prev, nurseId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedNurses.length === filteredNurses.length) {
      setSelectedNurses([])
    } else {
      setSelectedNurses(filteredNurses.map(n => n._id))
    }
  }

  const handleUnverifyNurse = async () => {
    if (!actionModal.nurse || !actionReason.trim()) {
      toast.error("Please provide a reason")
      return
    }

    setActionLoading(true)
    try {
      const adminEmail = lumi.auth.user?.email || "Unknown Admin"
      const now = new Date().toISOString()

      // Update nurse profile directly
      await lumi.entities.UserProfiles.update(actionModal.nurse._id, {
        verified: false,
        unverifiedAt: now,
        unverifiedBy: adminEmail,
        unverifyReason: actionReason
      })

      // Create audit log
      await lumi.entities.AuditLogs.create({
        actor_id: lumi.auth.user?.userId || "",
        action: "nurse_unverified",
        target_id: actionModal.nurse._id,
        metadata: {
          timestamp: now,
          reason: actionReason,
          nurseName: actionModal.nurse.fullName,
          nurseEmail: actionModal.nurse.email
        }
      })

      // Send email notification if requested
      if (notifyNurse && actionModal.nurse.email) {
        await lumi.tools.email.send({
          to: actionModal.nurse.email,
          subject: "Nursing Hive - Profile Verification Status Update",
          fromName: "Nursing Hive Team",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #0FB5AE;">Profile Verification Update</h2>
              <p>Dear ${actionModal.nurse.fullName || "Nurse"},</p>
              <p>Your Nursing Hive profile verification has been removed.</p>
              <p><strong>Reason:</strong> ${actionReason}</p>
              <p>If you have questions, please contact us at hello@nursinghive.com</p>
              <p>Best regards,<br/>Nursing Hive Team</p>
            </div>
          `
        }).catch(err => console.error("Email failed:", err))
      }

      toast.success("Nurse unverified successfully")
      closeModal()
      loadNurses()
    } catch (error: any) {
      console.error("Failed to unverify:", error)
      toast.error(error?.message || "Failed to unverify nurse")
    } finally {
      setActionLoading(false)
    }
  }

  const handleRejectNurse = async () => {
    if (!actionModal.nurse || !actionReason.trim()) {
      toast.error("Please provide a reason")
      return
    }

    setActionLoading(true)
    try {
      const adminEmail = lumi.auth.user?.email || "Unknown Admin"
      const now = new Date().toISOString()

      // Update nurse profile
      await lumi.entities.UserProfiles.update(actionModal.nurse._id, {
        status: "rejected",
        rejectedAt: now,
        rejectedBy: adminEmail,
        rejectReason: actionReason,
        verified: false
      })

      // Create audit log (non-blocking)
      lumi.entities.AuditLogs.create({
        actor_id: lumi.auth.user?.userId || "",
        action: "nurse_rejected",
        target_id: actionModal.nurse._id,
        metadata: {
          timestamp: now,
          reason: actionReason,
          nurseName: actionModal.nurse.fullName,
          nurseEmail: actionModal.nurse.email
        }
      }).catch(err => console.error("Audit log failed:", err))

      // Send email notification
      if (notifyNurse && actionModal.nurse.email) {
        await lumi.tools.email.send({
          to: actionModal.nurse.email,
          subject: "Nursing Hive - Application Status Update",
          fromName: "Nursing Hive Team",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #0FB5AE;">Application Status Update</h2>
              <p>Dear ${actionModal.nurse.fullName || "Nurse"},</p>
              <p>We regret to inform you that your application has been rejected.</p>
              <p><strong>Reason:</strong> ${actionReason}</p>
              <p>If you have questions, please contact us at hello@nursinghive.com</p>
              <p>Best regards,<br/>Nursing Hive Team</p>
            </div>
          `
        }).catch(err => console.error("Email failed:", err))
      }

      toast.success("Nurse application rejected")
      closeModal()
      loadNurses()
    } catch (error: any) {
      console.error("Failed to reject:", error)
      toast.error(error?.message || "Failed to reject application")
    } finally {
      setActionLoading(false)
    }
  }

  const handleBlockNurse = async () => {
    if (!actionModal.nurse || !actionReason.trim()) {
      toast.error("Please provide a reason")
      return
    }

    setActionLoading(true)
    try {
      const adminEmail = lumi.auth.user?.email || "Unknown Admin"
      const now = new Date().toISOString()

      // Update nurse profile
      await lumi.entities.UserProfiles.update(actionModal.nurse._id, {
        blocked: true,
        blockedAt: now,
        blockedBy: adminEmail,
        blockReason: actionReason,
        permanentBan: permanentBan,
        status: "blocked"
      })

      // Create audit log
      await lumi.entities.AuditLogs.create({
        actor_id: lumi.auth.user?.userId || "",
        action: permanentBan ? "nurse_banned" : "nurse_blocked",
        target_id: actionModal.nurse._id,
        metadata: {
          timestamp: now,
          reason: actionReason,
          permanent: permanentBan,
          nurseName: actionModal.nurse.fullName,
          nurseEmail: actionModal.nurse.email
        }
      })

      // Send email notification
      if (notifyNurse && actionModal.nurse.email) {
        await lumi.tools.email.send({
          to: actionModal.nurse.email,
          subject: "Nursing Hive - Account Status Update",
          fromName: "Nursing Hive Team",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #0FB5AE;">Account Status Update</h2>
              <p>Dear ${actionModal.nurse.fullName || "Nurse"},</p>
              <p>Your Nursing Hive account has been ${permanentBan ? 'permanently banned' : 'temporarily blocked'}.</p>
              <p><strong>Reason:</strong> ${actionReason}</p>
              <p>If you believe this is an error, please contact us at hello@nursinghive.com</p>
              <p>Best regards,<br/>Nursing Hive Team</p>
            </div>
          `
        }).catch(err => console.error("Email failed:", err))
      }

      toast.success(permanentBan ? "Nurse permanently banned" : "Nurse blocked successfully")
      closeModal()
      loadNurses()
    } catch (error: any) {
      console.error("Failed to block:", error)
      toast.error(error?.message || "Failed to block nurse")
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteNurse = async () => {
    if (!actionModal.nurse) return

    setActionLoading(true)
    try {
      const adminEmail = lumi.auth.user?.email || "Unknown Admin"
      const now = new Date().toISOString()

      // Create audit log before deletion
      await lumi.entities.AuditLogs.create({
        actor_id: lumi.auth.user?.userId || "",
        action: "nurse_deleted",
        target_id: actionModal.nurse._id,
        metadata: {
          timestamp: now,
          reason: actionReason,
          nurseName: actionModal.nurse.fullName,
          nurseEmail: actionModal.nurse.email,
          backupData: JSON.stringify(actionModal.nurse)
        }
      })

      // Delete the profile
      await lumi.entities.UserProfiles.delete(actionModal.nurse._id)

      toast.success("Nurse profile deleted")
      closeModal()
      loadNurses()
    } catch (error: any) {
      console.error("Failed to delete:", error)
      toast.error(error?.message || "Failed to delete profile")
    } finally {
      setActionLoading(false)
    }
  }

  const handleUnblockNurse = async (nurseId: string) => {
    if (!confirm("Are you sure you want to unblock this nurse?")) return

    try {
      const nurse = nurses.find(n => n._id === nurseId)
      if (!nurse) {
        toast.error("Nurse not found")
        return
      }

      if (nurse.permanentBan) {
        toast.error("Cannot unblock permanently banned nurse")
        return
      }

      const adminEmail = lumi.auth.user?.email || "Unknown Admin"
      const now = new Date().toISOString()

      // Update nurse profile
      await lumi.entities.UserProfiles.update(nurseId, {
        blocked: false,
        unblockedAt: now,
        unblockedBy: adminEmail,
        status: "active"
      })

      // Create audit log (non-blocking)
      lumi.entities.AuditLogs.create({
        actor_id: lumi.auth.user?.userId || "",
        action: "nurse_unblocked",
        target_id: nurseId,
        metadata: {
          timestamp: now,
          nurseName: nurse.fullName,
          nurseEmail: nurse.email
        }
      }).catch(err => console.error("Audit log failed:", err))

      // Send email notification
      if (nurse.email) {
        await lumi.tools.email.send({
          to: nurse.email,
          subject: "Nursing Hive - Account Unblocked",
          fromName: "Nursing Hive Team",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #0FB5AE;">Account Unblocked</h2>
              <p>Dear ${nurse.fullName || "Nurse"},</p>
              <p>Your Nursing Hive account has been unblocked. You can now access your profile.</p>
              <p>Best regards,<br/>Nursing Hive Team</p>
            </div>
          `
        }).catch(err => console.error("Email failed:", err))
      }

      toast.success("Nurse unblocked successfully")
      loadNurses()
    } catch (error: any) {
      console.error("Failed to unblock:", error)
      toast.error(error?.message || "Failed to unblock nurse")
    }
  }

  const handleResetPassword = async (userId: string, email: string) => {
    if (!confirm(`Send password reset email to ${email}?`)) return
    
    try {
      // TODO: Implement password reset via backend API
      toast.success("Password reset email sent")
      
      await lumi.entities.AuditLogs.create({
        actor_id: lumi.auth.user?.userId || "",
        action: "reset_password",
        target_id: userId,
        metadata: {
          timestamp: new Date().toISOString(),
          email
        }
      })
    } catch (error) {
      console.error("Failed to reset password:", error)
      toast.error("Failed to send reset email")
    }
  }

  const openModal = (type: ActionModal['type'], nurse: NurseProfile) => {
    setActionModal({ type, nurse })
    setActionReason("")
    setNotifyNurse(true)
    setPermanentBan(false)
  }

  const closeModal = () => {
    setActionModal({ type: null, nurse: null })
    setActionReason("")
    setNotifyNurse(true)
    setPermanentBan(false)
  }

  const handleSignOut = () => {
    lumi.auth.signOut()
    toast.success("Signed out successfully")
    navigate("/")
  }

  const filteredNurses = nurses.filter(nurse => {
    const matchesSearch = searchTerm === "" || 
      nurse.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nurse.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      nurse.country?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = filterStatus === "all" ||
      (filterStatus === "verified" && nurse.verified) ||
      (filterStatus === "pending" && !nurse.verified)
    
    // Only show nurse profiles (filter out admins/recruiters/affiliates)
    const isNurse = nurse.role === "nurse" || !nurse.role
    
    return matchesSearch && matchesFilter && isNurse
  })

  const renderOverview = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>Live • Updates every 45s</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-lg shadow-sm border-2 border-teal-200">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-teal-600" />
            {loading ? (
              <div className="animate-pulse h-9 bg-teal-300 rounded w-16"></div>
            ) : (
              <span className="text-3xl font-bold text-teal-900">{stats.total_nurses}</span>
            )}
          </div>
          <p className="text-teal-700 font-medium">Total Nurses</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow-sm border-2 border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-blue-600" />
            {loading ? (
              <div className="animate-pulse h-9 bg-blue-300 rounded w-16"></div>
            ) : (
              <span className="text-3xl font-bold text-blue-900">{stats.nurses_7d}</span>
            )}
          </div>
          <p className="text-blue-700 font-medium">New (7d)</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow-sm border-2 border-green-200">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-8 h-8 text-green-600" />
            {loading ? (
              <div className="animate-pulse h-9 bg-green-300 rounded w-16"></div>
            ) : (
              <span className="text-3xl font-bold text-green-900">{stats.verified_nurses}</span>
            )}
          </div>
          <p className="text-green-700 font-medium">Verified</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg shadow-sm border-2 border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-8 h-8 text-purple-600" />
            {loading ? (
              <div className="animate-pulse h-9 bg-purple-300 rounded w-16"></div>
            ) : (
              <span className="text-3xl font-bold text-purple-900">{stats.nclex_passed}</span>
            )}
          </div>
          <p className="text-purple-700 font-medium">NCLEX Passed</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => setActiveTab("nurses")}
            className="p-4 border border-gray-200 rounded-lg hover:bg-teal-50 hover:border-teal-300 transition-colors text-left"
          >
            <Users className="w-6 h-6 text-teal-600 mb-2" />
            <div className="font-medium text-gray-900">View All Nurses</div>
            <div className="text-sm text-gray-600">Manage nurse profiles</div>
          </button>
          <button
            onClick={() => setActiveTab("recruiters")}
            className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
          >
            <Users className="w-6 h-6 text-blue-600 mb-2" />
            <div className="font-medium text-gray-900">Manage Recruiters</div>
            <div className="text-sm text-gray-600">View recruiter applications</div>
          </button>
          <button
            onClick={() => setActiveTab("affiliates")}
            className="p-4 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 transition-colors text-left"
          >
            <Users className="w-6 h-6 text-purple-600 mb-2" />
            <div className="font-medium text-gray-900">Manage Affiliates</div>
            <div className="text-sm text-gray-600">View affiliate applications</div>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Activity</h3>
        <p className="text-gray-600">No recent activity</p>
      </div>
    </div>
  )

  const renderNurses = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Nurse Profiles</h2>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-teal-600" />
            <span className="text-3xl font-bold text-gray-900">{stats.total_nurses}</span>
          </div>
          <p className="text-gray-600">Total Nurses</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <span className="text-3xl font-bold text-gray-900">{stats.verified_nurses}</span>
          </div>
          <p className="text-gray-600">Verified</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-8 h-8 text-yellow-600" />
            <span className="text-3xl font-bold text-gray-900">{stats.pending_verification}</span>
          </div>
          <p className="text-gray-600">Pending Verification</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or country..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Nurses Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Specialty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Certifications</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                  </td>
                </tr>
              ) : filteredNurses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No nurses found
                  </td>
                </tr>
              ) : (
                filteredNurses.map((nurse) => (
                  <tr key={nurse._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{nurse.fullName || "N/A"}</div>
                      <div className="text-sm text-gray-500">{nurse.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {nurse.country || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {nurse.specialties?.join(", ") || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          nurse.nclexStatus === "Passed" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                        }`}>
                          NCLEX: {nurse.nclexStatus || "N/A"}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          nurse.cgfnsStatus === "Passed" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"
                        }`}>
                          CGFNS: {nurse.cgfnsStatus || "N/A"}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          nurse.dataflowStatus === "Complete" ? "bg-purple-100 text-purple-800" : "bg-gray-100 text-gray-800"
                        }`}>
                          Dataflow: {nurse.dataflowStatus || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {nurse.verified ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex flex-wrap gap-2">
                        {!nurse.verified && !nurse.blocked && (
                          <button
                            onClick={() => handleVerifyNurse(nurse._id)}
                            className="text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Verify
                          </button>
                        )}
                        {nurse.verified && (
                          <button
                            onClick={() => openModal('unverify', nurse)}
                            className="text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1"
                          >
                            <XCircle className="w-3 h-3" />
                            Unverify
                          </button>
                        )}
                        {!nurse.blocked && (
                          <>
                            <button
                              onClick={() => openModal('reject', nurse)}
                              className="text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                            >
                              <XCircle className="w-3 h-3" />
                              Reject
                            </button>
                            <button
                              onClick={() => openModal('block', nurse)}
                              className="text-red-700 hover:text-red-900 font-medium flex items-center gap-1"
                            >
                              <Ban className="w-3 h-3" />
                              Block
                            </button>
                          </>
                        )}
                        {nurse.blocked && lumi.auth.user?.userRole === "ADMIN" && (
                          <button
                            onClick={() => handleUnblockNurse(nurse._id)}
                            className="text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Unblock
                          </button>
                        )}
                        {lumi.auth.user?.userRole === "ADMIN" && (
                          <button
                            onClick={() => openModal('delete', nurse)}
                            className="text-gray-600 hover:text-gray-800 font-medium flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderMessages = () => (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
    </div>
  )

  const renderRecruiters = () => (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Recruiters</h2>
      <p className="text-gray-600 mb-6">
        Manage recruiter applications and accounts
      </p>
      <div className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium">
        Coming Soon
      </div>
    </div>
  )

  const renderAffiliates = () => (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Affiliates</h2>
      <p className="text-gray-600 mb-6">
        Manage affiliate applications and partnerships
      </p>
      <div className="inline-block px-4 py-2 bg-purple-100 text-purple-800 rounded-lg font-medium">
        Coming Soon
      </div>
    </div>
  )

  const renderReports = () => (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Statistics</h2>

      {/* Certification Stats */}
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">NCLEX Passed</div>
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{stats.nclex_passed}</div>
          <div className="text-sm text-gray-500 mt-1">
            {stats.total_nurses > 0 ? Math.round((stats.nclex_passed / stats.total_nurses) * 100) : 0}% of total
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">CGFNS Completed</div>
            <CheckCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{stats.cgfns_completed}</div>
          <div className="text-sm text-gray-500 mt-1">
            {stats.total_nurses > 0 ? Math.round((stats.cgfns_completed / stats.total_nurses) * 100) : 0}% of total
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-gray-600">Dataflow Completed</div>
            <CheckCircle className="w-6 h-6 text-purple-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{stats.dataflow_completed}</div>
          <div className="text-sm text-gray-500 mt-1">
            {stats.total_nurses > 0 ? Math.round((stats.dataflow_completed / stats.total_nurses) * 100) : 0}% of total
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">Detailed Analytics</h3>
        <p className="text-gray-600 mb-6">
          Advanced reporting and trend analysis
        </p>
        <div className="inline-block px-4 py-2 bg-purple-100 text-purple-800 rounded-lg font-medium">
          Coming Soon
        </div>
      </div>
    </div>
  )



  const renderSettings = () => (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Settings</h2>
      <p className="text-gray-600 mb-6">
        Configure dashboard preferences and notifications
      </p>
      <div className="inline-block px-4 py-2 bg-gray-100 text-gray-800 rounded-lg font-medium">
        Coming Soon
      </div>
    </div>
  )

  const renderActionModal = () => {
    if (!actionModal.type || !actionModal.nurse) return null

    const modalConfig = {
      unverify: {
        title: "Unverify Nurse",
        description: `Remove verification from ${actionModal.nurse.fullName}?`,
        color: "orange",
        action: handleUnverifyNurse,
        requireReason: true
      },
      reject: {
        title: "Reject Application",
        description: `Reject application from ${actionModal.nurse.fullName}?`,
        color: "red",
        action: handleRejectNurse,
        requireReason: true
      },
      block: {
        title: "Block Nurse",
        description: `Block ${actionModal.nurse.fullName} from accessing the platform?`,
        color: "red",
        action: handleBlockNurse,
        requireReason: true
      },
      delete: {
        title: "Delete Profile",
        description: `Permanently delete ${actionModal.nurse.fullName}'s profile? This action cannot be undone.`,
        color: "gray",
        action: handleDeleteNurse,
        requireReason: false
      }
    }

    const config = modalConfig[actionModal.type]

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className={`w-6 h-6 text-${config.color}-600`} />
            <h3 className="text-xl font-bold text-gray-900">{config.title}</h3>
          </div>
          
          <p className="text-gray-600 mb-4">{config.description}</p>
          
          <div className="space-y-4">
            {config.requireReason && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Provide a reason for this action..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  rows={3}
                  required
                />
              </div>
            )}

            {actionModal.type === 'block' && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={permanentBan}
                  onChange={(e) => setPermanentBan(e.target.checked)}
                  className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">Permanent ban (cannot be unblocked)</span>
              </label>
            )}

            {config.requireReason && (
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={notifyNurse}
                  onChange={(e) => setNotifyNurse(e.target.checked)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">Send email notification to nurse</span>
              </label>
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={closeModal}
              disabled={actionLoading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={config.action}
              disabled={actionLoading || (config.requireReason && !actionReason.trim())}
              className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${
                config.color === 'orange' ? 'bg-orange-600 hover:bg-orange-700' :
                config.color === 'red' ? 'bg-red-600 hover:bg-red-700' :
                config.color === 'gray' ? 'bg-gray-600 hover:bg-gray-700' :
                'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              {actionLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                config.title
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://i.imghippo.com/files/KBd1355nw.png"
              alt="Nursing Hive"
              className="w-10 h-auto"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <DashboardSwitcher />
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow-sm p-4 space-y-1">
              <button
                onClick={() => setActiveTab("overview")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "overview" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span className="font-medium">Overview</span>
              </button>
              <button
                onClick={() => setActiveTab("nurses")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "nurses" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Nurses</span>
              </button>
              <button
                onClick={() => setActiveTab("messages")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "messages" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FileText className="w-5 h-5" />
                <span className="font-medium">Messages</span>
              </button>
              <button
                onClick={() => setActiveTab("recruiters")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "recruiters" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Recruiters</span>
              </button>
              <button
                onClick={() => setActiveTab("affiliates")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "affiliates" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Affiliates</span>
              </button>
              <button
                onClick={() => setActiveTab("reports")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "reports" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span className="font-medium">Reports</span>
              </button>
              <button
                onClick={() => navigate("/admin/contacts")}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-gray-700 hover:bg-gray-50"
              >
                <Mail className="w-5 h-5" />
                <span className="font-medium">Contacts</span>
              </button>
              <button
                onClick={() => setActiveTab("settings")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "settings" ? "bg-teal-50 text-teal-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Settings className="w-5 h-5" />
                <span className="font-medium">Settings</span>
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === "overview" && renderOverview()}
            {activeTab === "nurses" && renderNurses()}
            {activeTab === "messages" && renderMessages()}
            {activeTab === "recruiters" && renderRecruiters()}
            {activeTab === "affiliates" && renderAffiliates()}
            {activeTab === "reports" && renderReports()}
            {activeTab === "settings" && renderSettings()}
          </div>
        </div>
      </div>

      {/* Action Modal */}
      {renderActionModal()}
    </div>
  )
}
